import { useState } from "react";
import { Mail, Check, RotateCcw, ChevronDown, Phone, Trash2 } from "lucide-react";
import { replyMailto } from "@/lib/admin";

const fmt = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export const LeadRow = ({ lead, kind, onStatus, onDelete }) => {
  const [open, setOpen] = useState(false);
  const contacted = lead.status === "contacted";
  const isInq = kind === "inquiries";

  return (
    <li className={`border-b border-gold/10 transition-colors ${contacted ? "opacity-60" : "hover:bg-gold/[0.03]"}`} data-testid={`lead-row-${lead.id}`}>
      <div className="grid grid-cols-[1fr_auto] md:grid-cols-[9rem_1fr_11rem_auto] gap-x-6 gap-y-3 items-center px-4 py-4">
        <span className="font-mono text-[11px] text-ivory/40 hidden md:block">{fmt(lead.created_at)}</span>
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-serif text-lg text-ivory">{lead.name || "Visitor"}</span>
            <span className={`font-mono text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 border ${contacted ? "border-ivory/20 text-ivory/40" : "border-gold/60 text-gold"}`} data-testid={`lead-status-${lead.id}`}>
              {lead.status}
            </span>
            {isInq && <span className="text-xs text-ivory/50">{lead.interest}</span>}
          </div>
          <div className="mt-1 flex items-center gap-4 text-xs text-ivory/60 truncate">
            <span className="truncate">{lead.email}</span>
            {lead.phone && (
              <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1 hover:text-gold">
                <Phone size={11} /> {lead.phone}
              </a>
            )}
          </div>
        </div>
        <span className="hidden md:block text-xs text-ivory/40">{isInq ? "Contact form" : "Deck download"}</span>
        <div className="flex items-center gap-2 justify-end">
          <a
            href={replyMailto(lead, kind)}
            onClick={() => !contacted && onStatus(lead, "contacted")}
            className="inline-flex items-center gap-2 bg-gold text-obsidian text-[10px] tracking-[0.18em] uppercase px-3 py-2 hover:bg-gold-light transition-colors"
            data-testid={`lead-reply-${lead.id}`}
          >
            <Mail size={12} /> Reply
          </a>
          <button
            onClick={() => onStatus(lead, contacted ? "new" : "contacted")}
            className="inline-flex items-center gap-2 border border-ivory/20 text-ivory/70 text-[10px] tracking-[0.18em] uppercase px-3 py-2 hover:border-gold hover:text-gold transition-colors"
            data-testid={`lead-toggle-${lead.id}`}
            title={contacted ? "Mark as new" : "Mark as contacted"}
          >
            {contacted ? <RotateCcw size={12} /> : <Check size={12} />}
            <span className="hidden sm:inline">{contacted ? "Reopen" : "Done"}</span>
          </button>
          <button
            onClick={() => window.confirm(`Delete ${lead.name || lead.email}? This cannot be undone.`) && onDelete(lead)}
            className="p-2 text-ivory/40 hover:text-red-400 transition-colors"
            aria-label="Delete lead"
            title="Delete"
            data-testid={`lead-delete-${lead.id}`}
          >
            <Trash2 size={13} />
          </button>
          {isInq && (
            <button onClick={() => setOpen((o) => !o)} className="p-2 text-ivory/50 hover:text-gold" aria-label="Toggle message" data-testid={`lead-expand-${lead.id}`}>
              <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>
      </div>
      {isInq && open && (
        <p className="px-4 pb-5 md:pl-[13.5rem] text-sm text-ivory/70 leading-relaxed whitespace-pre-wrap" data-testid={`lead-message-${lead.id}`}>
          {lead.message}
        </p>
      )}
    </li>
  );
};
